/**
 * verteilHelpers.js
 * Pure utility functions for Verteil (NDC) vendor-specific data transformations.
 * Extracted from PassengerListPage.js — no component dependencies.
 */

/**
 * Processes Verteil fare rules segment penalty data into a structured format.
 * @param {Object} segmentPenaltyArr - Object keyed by segmentId containing penalty data
 * @param {Array}  tripInfos         - Array of trip info objects with segments
 * @returns {Object} processedFareRules - Keyed by route string (e.g. "AMD-DXB")
 */
export const processVerteilFareRules = (segmentPenaltyArr, tripInfos) => {
    const processedFareRules = {};

    // Get all segments from tripInfos
    const allSegments = [];
    tripInfos?.forEach((trip, tripIndex) => {
        trip?.segments?.forEach((segment, segmentIndex) => {
            allSegments.push({
                ...segment,
                tripIndex,
                segmentIndex,
                segmentKey: `${segment.departure.airportCode}-${segment.arrival.airportCode}`
            });
        });
    });

    // Find the first segment that has penalty data to use as template
    let templatePenaltyData = null;

    // Look for any segment penalty data in the provided data
    Object.keys(segmentPenaltyArr).forEach(segmentId => {
        if (segmentPenaltyArr[segmentId] && !templatePenaltyData) {
            templatePenaltyData = segmentPenaltyArr[segmentId];
        }
    });

    // Check if we have multiple segments but only one has data
    const segmentsWithData = Object.keys(segmentPenaltyArr).length;
    const totalSegments = allSegments.length;

    if (templatePenaltyData) {
        if (segmentsWithData === 1 && totalSegments > 1) {
            // Create a combined route key (e.g., AMD-DXB for AMD-BOM-BOM-DEL-DEL-DXB)
            const firstSegment = allSegments[0];
            const lastSegment = allSegments[allSegments.length - 1];
            const combinedRouteKey = `${firstSegment.departure.airportCode}-${lastSegment.arrival.airportCode}`;

            // Process penalty data for each passenger type
            const processedSegmentRules = {};

            Object.keys(templatePenaltyData).forEach(passengerType => {
                const penalties = templatePenaltyData[passengerType];
                const processedPenalties = {
                    CANCELLATION: {},
                    DATECHANGE: {},
                    NO_SHOW: {}
                };

                penalties.forEach(penalty => {
                    const penaltyType = penalty.Type;
                    const application = penalty.Application;
                    const applicationDesc = penalty.ApplicationDesc;
                    const amounts = penalty.Amounts;

                    // Create a key for the penalty
                    const penaltyKey = `${penaltyType}_${application}_${applicationDesc}`.replace(/\s+/g, '_');

                    // Determine which category this penalty belongs to
                    let category = 'DATECHANGE';
                    if (penaltyType.includes('Cancel')) {
                        category = 'CANCELLATION';
                    } else if (penaltyType.includes('NoShow')) {
                        category = 'NO_SHOW';
                    }

                    // Format the penalty data
                    const formattedPenalty = {
                        amount: amounts?.MAX?.amount || amounts?.MIN?.amount || 0,
                        currency: amounts?.MAX?.code || amounts?.MIN?.code || 'INR',
                        policyInfo: `${penaltyType} - ${applicationDesc}`,
                        maxAmount: amounts?.MAX?.amount || 0,
                        minAmount: amounts?.MIN?.amount || 0
                    };

                    processedPenalties[category][penaltyKey] = formattedPenalty;
                });

                processedSegmentRules[passengerType] = processedPenalties;
            });

            processedFareRules[combinedRouteKey] = {
                fr: processedSegmentRules
            };
        } else {
            // Process each segment individually (original logic)
            allSegments.forEach((segment, index) => {
                const segmentKey = segment.segmentKey;

                // Use template penalty data for all segments since verteil provides the same rules for all segments
                const penaltyData = templatePenaltyData;

                if (penaltyData) {
                    // Process penalty data for each passenger type
                    const processedSegmentRules = {};

                    Object.keys(penaltyData).forEach(passengerType => {
                        const penalties = penaltyData[passengerType];
                        const processedPenalties = {
                            CANCELLATION: {},
                            DATECHANGE: {},
                            NO_SHOW: {}
                        };

                        penalties.forEach(penalty => {
                            const penaltyType = penalty.Type;
                            const application = penalty.Application;
                            const applicationDesc = penalty.ApplicationDesc;
                            const amounts = penalty.Amounts;

                            // Create a key for the penalty
                            const penaltyKey = `${penaltyType}_${application}_${applicationDesc}`.replace(/\s+/g, '_');

                            // Determine which category this penalty belongs to
                            let category = 'DATECHANGE';
                            if (penaltyType.includes('Cancel')) {
                                category = 'CANCELLATION';
                            } else if (penaltyType.includes('NoShow')) {
                                category = 'NO_SHOW';
                            }

                            // Format the penalty data
                            const formattedPenalty = {
                                amount: amounts?.MAX?.amount || amounts?.MIN?.amount || 0,
                                currency: amounts?.MAX?.code || amounts?.MIN?.code || 'INR',
                                policyInfo: `${penaltyType} - ${applicationDesc}`,
                                maxAmount: amounts?.MAX?.amount || 0,
                                minAmount: amounts?.MIN?.amount || 0
                            };

                            processedPenalties[category][penaltyKey] = formattedPenalty;
                        });

                        processedSegmentRules[passengerType] = processedPenalties;
                    });

                    processedFareRules[segmentKey] = {
                        fr: processedSegmentRules
                    };
                }
            });
        }
    }

    return processedFareRules;
};
